import React, {useEffect, useMemo, useRef} from 'react';
import type {SxProps, ToolbarProps} from '@mui/material';
import type {
  GridValidRowModel,
  ToolbarPropsOverrides,
} from '@mui/x-data-grid-pro';
import {
  Toolbar,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  gridFilteredSortedTopLevelRowEntriesSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import isEqual from 'lodash/isEqual';
import {FlexBox, Layout} from 'components/layout';
import {Text} from 'components/text';
import {IconTool} from 'components/toolbar';
import type {PaginationModel} from '../types';
import {mapColumnSelectionOptions} from '../functions/mapColumnSelectionOptions';
import {useColumnSelectionPopover} from '../hooks';
import {DataGridPaginator} from './DataGridPaginator';

declare module '@mui/x-data-grid-pro' {
  interface ToolbarPropsOverrides {
    setPaginationModel: (model: PaginationModel) => void;
    header?: React.ReactNode | string;
    columnSelectionTitle?: string;
    hiddenColumns?: string[];
    hideColumnSelector: boolean;
    onRowsUpdate?: (gridRows: GridValidRowModel[]) => void;
    scrollAfterPagination?: boolean;
  }
}

const toolbarActionsSx: SxProps = {
  flexDirection: 'row',
  alignItems: 'center',
};

const headerSx: SxProps = {
  flex: 'auto',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 2,
};

export function CustomGridToolbar({
  setPaginationModel,
  header,
  columnSelectionTitle,
  hiddenColumns,
  hideColumnSelector,
  onRowsUpdate,
  scrollAfterPagination,
}: ToolbarProps<typeof Toolbar, ToolbarPropsOverrides>) {
  const {headerNode} = useMemo(() => {
    const headerNode =
      typeof header === 'string' ? (
        <Text variant={'h3'}>{header}</Text>
      ) : (
        (header ?? <Text variant={'h3'} />)
      );

    return {
      headerNode,
    };
  }, [header]);

  const apiRef = useGridApiContext();

  /**
   * Sometimes we need to report back the current rows (after custom task filtering, and filtermodel) with current data.
   */
  const rowsRef = useRef<GridValidRowModel[]>([]);
  const gridRows = gridFilteredSortedTopLevelRowEntriesSelector(apiRef);
  useEffect(() => {
    if (onRowsUpdate && gridRows.length > 0) {
      const rowModels = gridRows.map(r => r.model);
      if (!isEqual(rowsRef.current, rowModels)) {
        rowsRef.current = rowModels;
        onRowsUpdate(rowModels);
      }
    }
  }, [gridRows, onRowsUpdate]);

  /* Column selection */
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const columnVisibilityModel = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector,
  );

  const options = mapColumnSelectionOptions(
    columns,
    columnVisibilityModel,
  ).filter(c => !hiddenColumns?.includes(c.key));
  const {
    open: openSelectColumns,
    Popover: SelectColumnsPopover,
    popoverProps: selectColumnsProps,
  } = useColumnSelectionPopover(
    options,
    apiRef.current.setColumnVisibility,
    columnSelectionTitle,
  );

  return (
    <Toolbar>
      <FlexBox sx={headerSx}>{headerNode}</FlexBox>
      <FlexBox sx={toolbarActionsSx}>
        {DataGridPaginator(setPaginationModel, scrollAfterPagination)}

        {!hideColumnSelector ? (
          // Maybe change to ColumnsPanelTrigger in future
          <Layout>
            <IconTool icon="MetadataSelection" onClick={openSelectColumns} />
            <SelectColumnsPopover {...selectColumnsProps} />
          </Layout>
        ) : null}
      </FlexBox>
    </Toolbar>
  );
}
